import GenericHead from 'components/GenericHead';
import RichResults from 'components/RichResults';
import useTranslate from 'contexts/TranslateContext/useTranslate';
import { Genre } from 'state/Genres/types';
import { get } from 'lodash-es';
import { getScaledImageUrlById } from 'utils/url';
import { Helmet } from 'react-helmet';
import { highlightsPlayerLink } from 'utils/HighlightsUtils';
import { Market } from 'state/Location/types';
import { link as playerLink, meta as playerMeta } from 'heads/player';
import { STATION_TYPE } from 'constants/stationTypes';
import { translateKeywords } from 'utils/metaUtils';
import { ViewName } from 'views/PageBody';
import {
  WIDGET_DIMENSIONS,
  WidgetDimensions,
} from 'constants/widgetDimensions';
import type { HighlightsMetadata } from 'api/highlights';

export type Props = {
  band: string;
  callLetters: string;
  city: string;
  highlightsMetadata: HighlightsMetadata | null;
  description: string;
  freq: string;
  genre: Genre | Record<string, any>;
  id: string;
  isSubSection?: boolean;
  logo: string;
  market: Market;
  metaTitle?: string;
  pagePath: string;
  seedId: string | number;
  seedType: string;
  siteUrl: string;
  stateAbbreviation: string;
  stationName: string;
  website: string;
};

export default function LiveHighlightsHead({
  band,
  callLetters,
  city,
  highlightsMetadata,
  description,
  freq,
  genre,
  id,
  isSubSection = false,
  logo,
  market,
  metaTitle,
  pagePath,
  seedId,
  seedType,
  siteUrl,
  stateAbbreviation,
  stationName,
  website,
}: Props) {
  const translate = useTranslate();

  const title = metaTitle || stationName;

  // Use clip metadata if available, otherwise use station metadata
  const pageTitle = highlightsMetadata?.title || title;
  const pageDescription = highlightsMetadata?.description || description;
  const pageImage =
    highlightsMetadata?.preview_image ||
    (highlightsMetadata?.image ?
      `${highlightsMetadata.image}?ops=contain(1200,630)`
    : '') ||
    (logo && logo.startsWith('//') ? `https:${logo}` : logo) ||
    getScaledImageUrlById(seedId as unknown as number, seedType);

  const keywords = translateKeywords(
    translate,
    'iHeartRadio, iHeart, Listen, Live, Music, Talk, Radio, Online, Streaming, Free',
  );
  const dynamicKeywords = translate(
    '{stationName}, {stateAbbreviation}, {city}, {genre}',
    {
      stationName,
      stateAbbreviation,
      city,
      genre: get(genre, 'name', ''),
    },
  );
  const compositeKeywords = `${dynamicKeywords}, ${keywords}`;

  const deeplink = `goto/live/${seedId}/highlights`;

  const { height, fixedWidth: width }: WidgetDimensions =
    WIDGET_DIMENSIONS.LIVE ?? {};

  return (
    <>
      <GenericHead
        deeplink={deeplink}
        description={pageDescription}
        facebookImage={pageImage}
        image={pageImage}
        ogType="video"
        title={pageTitle}
        twitterImage={pageImage}
      />
      <Helmet
        link={[
          ...playerLink({ pagePath, siteUrl, title: pageTitle }),
          // Preconnect to Highlights SDK domains for faster loading
          ...highlightsPlayerLink(),
        ]}
        meta={[
          {
            content: pageTitle,
            itemProp: 'name',
          },
          {
            content: compositeKeywords,
            name: 'keywords',
          },
          ...playerMeta({ pagePath, siteUrl, height, width }),
        ]}
      />
      {!isSubSection && (
        <RichResults
          meta={{
            broadcastDisplayName: stationName,
            broadcastFrequency: { band, frequency: freq },
            callLetters,
            description,
            id,
            keywords: `${dynamicKeywords}, ${keywords}`,
            market,
            seedType: STATION_TYPE.LIVE,
            stationName,
            url: pagePath,
            website,
          }}
          type={ViewName.LiveProfile}
        />
      )}
    </>
  );
}
